import React, { useState } from "react";
import { Flag, X } from "lucide-react";
import { api } from "@/lib/api";

const CATEGORIES = [
  { id: "copyright", label: "Copyright / unauthorized music" },
  { id: "abusive", label: "Hate speech, harassment, or abusive content" },
  { id: "spam", label: "Spam or fake station" },
  { id: "other", label: "Something else" },
];

export default function ReportButton({ stationId, trackId, compact = false }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("copyright");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post("/reports", { station_id: stationId || null, track_id: trackId || null, category, reason: reason.trim() });
      setDone(true);
    } catch (_) {}
    setBusy(false);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={compact
          ? "text-slate-500 hover:text-red-400 text-xs inline-flex items-center gap-1"
          : "fl-btn ghost !py-2 !px-3 text-xs"}
        data-testid="report-open"
        title="Report this content"
      >
        <Flag className="w-3.5 h-3.5" /> Report
      </button>

      {open && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setOpen(false)} data-testid="report-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#0a0a0a] border border-red-500/30 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <div className="flex items-center gap-2">
                <Flag className="w-4 h-4 text-red-400" />
                <span className="fl-display font-bold">Report content</span>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white" data-testid="report-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            {done ? (
              <div className="px-5 py-8 text-center space-y-3">
                <div className="text-sm text-emerald-300">Thanks. Our team will review it shortly.</div>
                <button onClick={() => { setOpen(false); setDone(false); setReason(""); }} className="fl-btn ghost !py-2 !px-4 text-sm" data-testid="report-done-close">Close</button>
              </div>
            ) : (
              <form onSubmit={submit} className="px-5 py-5 space-y-4">
                <div>
                  <label className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Reason</label>
                  <div className="mt-2 space-y-1.5">
                    {CATEGORIES.map(c => (
                      <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="radio" name="cat" value={c.id} checked={category === c.id} onChange={() => setCategory(c.id)} className="accent-red-500" />
                        {c.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Details</label>
                  <textarea
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="What's the problem? (3–500 chars)"
                    className="fl-input mt-1 text-sm w-full"
                    minLength={3}
                    maxLength={500}
                    required
                    data-testid="report-reason"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => setOpen(false)} className="fl-btn ghost !py-2 !px-3 text-sm">Cancel</button>
                  <button type="submit" disabled={busy} className="fl-btn !py-2 !px-3 text-sm" data-testid="report-submit">
                    {busy ? "Sending…" : "Submit report"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
